import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencyOptions } from '../../shared/utils/brandCurrency';
import {
  formatComptabiliteDate,
  formatComptabiliteMoney,
  getComptabiliteDateLocale } from './utils/comptabiliteSaudiFormat';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, Search, X, BarChart3, Wallet, TrendingUp, Percent, Filter, ChevronDown, ChevronUp, Download, Upload, FileText, FileSpreadsheet, FileOutput, List, LayoutList, Layers, Database, Target, RefreshCcw, Calendar, CheckCircle, LayoutGrid, XCircle, AlertTriangle, Award, Clock } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getBilans,
  createBilan,
  updateBilan,
  deleteBilan,
  computeBilanFromEntries,
  getCurrentFiscalYear,
} from '../../shared/utils/comptabiliteService';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import {
  EMPTY_BILAN_FORM,
  buildBilanPayload,
  recordToFormData } from './06-bilan-comptable/bilanComptablePayload';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

const STATUT_FILTER_VALUES = {
  provisoire: 'Provisoire',
  valide: 'Validé',
  certifie: 'Certifié' };

const BILAN_LIST_COLUMNS = [
  { key: 'bilan_ref', labelKey: 'col_reference' },
  { key: 'exercice_fiscal', labelKey: 'col_exercice_fiscal' },
  { key: 'date_etablissement', labelKey: 'col_date_etablissement' },
  { key: 'total_actif', labelKey: 'col_total_actif' },
  { key: 'total_passif', labelKey: 'col_total_passif' },
  { key: 'statut_bilan', labelKey: 'col_status' },
];

const BilanSituationPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  useComptabiliteConfig();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.bilanSituationPage.${key}`, opts);
  const dateLocale = getComptabiliteDateLocale(i18n.language);
  const fmtDate = (value) => formatComptabiliteDate(value, i18n.language);
  const fmtMoney = (value) => formatComptabiliteMoney(value);

  const statutLabelKeys = useMemo(() => ({
    Provisoire: 'status_provisoire',
    Définitif: 'status_definitif',
    Validé: 'status_valide',
    Audité: 'status_audite',
    Certifié: 'status_certifie' }), []);

  const typeLabelKeys = useMemo(() => ({
    Simplifié: 'type_simplifie',
    Normal: 'type_normal',
    Consolidé: 'type_consolide',
    Social: 'type_social',
    Fiscal: 'type_fiscal' }), []);

  const translateStatut = useCallback((val) => (val && statutLabelKeys[val] ? p(statutLabelKeys[val]) : val || p('na')), [p, statutLabelKeys]);
  const translateType = useCallback((val) => (val && typeLabelKeys[val] ? p(typeLabelKeys[val]) : val || p('not_specified')), [p, typeLabelKeys]);

  // ===== STATE MANAGEMENT =====
  const [bilans, setBilans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBilan, setSelectedBilan] = useState(null);
  const [viewingBilan, setViewingBilan] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Advanced Options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_bilans');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Date range filter
  const [setDateRange] = useState({ start: null, end: null });
  
  // Dropdowns
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Selection & Sorting
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('bilan_ref', (row) => row.bilan_ref ?? ''),
    textColumnRule('date_etablissement', (row) => row.date_etablissement ?? ''),
    textColumnRule('total_actif', (row) => String(row.total_actif ?? '')),
    textColumnRule('total_passif', (row) => String(row.total_passif ?? '')),
  ], []);
  
  // Expanded sections for GPS-style collapsible form
  const [bilanSituationFormTab, setBilanSituationFormTab] = useState('informationsBilan');
  const bilanSituationFormTabs = useMemo(() => ([
    { id: 'informationsBilan', label: p('section_info_bilan'), icon: ChevronUp },
    { id: 'actifImmobilisations', label: p('section_actif_immobilisations'), icon: ChevronUp },
    { id: 'passifRatios', label: p('section_ratios_analyse'), icon: ChevronUp }
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_BILAN_FORM });
  const [fiscalYearId, setFiscalYearId] = useState('');

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadBilans();
    getCurrentFiscalYear()
      .then((fy) => { if (fy?.id) setFiscalYearId(fy.id); })
      .catch(() => {});
  }, []);

  const syncBilanFromEntries = async () => {
    try {
      setLoading(true);
      const computed = await computeBilanFromEntries(fiscalYearId || null);
      if (computed && typeof computed === 'object') {
        setFormData((prev) => ({
          ...prev,
          ...recordToFormData(computed),
          exercice_fiscal: computed.exercice_fiscal || prev.exercice_fiscal,
          periode_debut: computed.periode_debut || prev.periode_debut,
          periode_fin: computed.periode_fin || prev.periode_fin,
        }));
        notifyUpdated(showNotify, p('sync_from_entries_success', { defaultValue: 'تم تحميل الميزانية من القيود' }));
      }
    } catch (error) {
      console.error('Error syncing bilan from entries:', error);
      notifyError(showNotify, p('sync_from_entries_error', { defaultValue: 'تعذر حساب الميزانية من القيود' }));
    } finally {
      setLoading(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Select handlers
  
  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteBilan(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'الميزانيات' }));
        setSelectedRecords([]);
        clearSelection();
        loadBilans();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };

  const loadBilans = async () => {
    try {
      setLoading(true);
      const response = await getBilans();
      setBilans(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading bilans:', error);
      setBilans([]);
    } finally {
      setLoading(false);
    }
  };

  const generateBilanReference = useCallback(() => {
    const year = new Date().getFullYear();
    const prefix = getEntityPrefix(settings, 'bilan', 'BILA');
    return generateEntityReference(`${prefix}-${year}`, bilans, (row) => row.bilan_ref);
  }, [bilans, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = buildBilanPayload(formData);
      if (!selectedBilan && !payload.bilan_ref?.trim()) {
        payload.bilan_ref = generateBilanReference();
      }
      
      if (selectedBilan) {
        await updateBilan(selectedBilan.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الميزانية' }));
      } else {
        await createBilan(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الميزانية' }));
      }
      await loadBilans();
      resetForm();
    } catch (error) {
      console.error('Error saving bilan:', error);
      notifyError(showNotify, p('save_error', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bilan) => {
    setSelectedBilan(bilan);
    setFormData(recordToFormData(bilan));
    setBilanSituationFormTab('informationsBilan');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await deleteBilan(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الميزانية' }));
        await loadBilans();
      } catch (error) {
        console.error('Error deleting bilan:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (bilan) => {
    setViewingBilan(bilan);
    setShowViewModal(true);
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const bilan = bilans.find((b) => b.id === recordId);
      if (!bilan) return;
      await updateBilan(recordId, { ...bilan, statut_bilan: newStatus });
      await loadBilans();
    } catch (error) {
      console.error('Error updating bilan status:', error);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_BILAN_FORM });
    setSelectedBilan(null);
    setShowModal(false);
    setBilanSituationFormTab('informationsBilan');
  };

  const openNewBilanModal = () => {
    setSelectedBilan(null);
    setFormData({ ...EMPTY_BILAN_FORM, bilan_ref: generateBilanReference() });
    setBilanSituationFormTab('informationsBilan');
    setShowModal(true);
  };

  // Stats
  const stats = {
    total: bilans.length,
    totalActif: bilans.reduce((sum, b) => sum + (b.total_actif || 0), 0),
    totalPassif: bilans.reduce((sum, b) => sum + (b.total_passif || 0), 0),
    capitauxPropres: bilans.reduce((sum, b) => sum + (b.capitaux_propres_total || 0), 0),
    fondsRoulement: bilans.reduce((sum, b) => sum + (b.fonds_roulement || 0), 0) / (bilans.length || 1)
  };

  const mainTabs = useMemo(() => [
    { id: 'all_bilans', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: CheckCircle },
    { id: 'par_type', nameKey: 'tab_by_type', icon: Building2 }
  ], []);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'provisoire', nameKey: 'filter_provisoire', icon: AlertTriangle },
    { id: 'valide', nameKey: 'filter_valide', icon: Award },
    { id: 'certifie', nameKey: 'filter_certifie', icon: CheckCircle }
  ], []);

  const bilanDistribution = useMemo(() => ({
    all_bilans: {
      titleKey: 'dist_overview',
      data: [
        { filterValue: 'Provisoire', nameKey: 'status_provisoire', count: bilans.filter(b => b.statut_bilan === 'Provisoire').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterValue: 'Validé', nameKey: 'status_valide', count: bilans.filter(b => b.statut_bilan === 'Validé').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'Définitif', nameKey: 'status_definitif', count: bilans.filter(b => b.statut_bilan === 'Définitif').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'Certifié', nameKey: 'status_certifie', count: bilans.filter(b => b.statut_bilan === 'Certifié').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    par_statut: {
      titleKey: 'dist_by_status',
      data: [
        { filterValue: 'Provisoire', nameKey: 'status_provisoire', count: bilans.filter(b => b.statut_bilan === 'Provisoire').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterValue: 'Définitif', nameKey: 'status_definitif', count: bilans.filter(b => b.statut_bilan === 'Définitif').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'Validé', nameKey: 'status_valide', count: bilans.filter(b => b.statut_bilan === 'Validé').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'Audité', nameKey: 'status_audite', count: bilans.filter(b => b.statut_bilan === 'Audité').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterValue: 'Certifié', nameKey: 'status_certifie', count: bilans.filter(b => b.statut_bilan === 'Certifié').length, color: "bg-gradient-to-r from-cyan-500 to-cyan-600" }
      ]
    },
    par_type: {
      titleKey: 'dist_by_type',
      data: [
        { filterValue: 'Simplifié', nameKey: 'type_simplifie', count: bilans.filter(b => b.type_bilan === 'Simplifié').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'Normal', nameKey: 'type_normal', count: bilans.filter(b => b.type_bilan === 'Normal').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'Consolidé', nameKey: 'type_consolide', count: bilans.filter(b => b.type_bilan === 'Consolidé').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterValue: 'Social', nameKey: 'type_social', count: bilans.filter(b => b.type_bilan === 'Social').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterValue: 'Fiscal', nameKey: 'type_fiscal', count: bilans.filter(b => b.type_bilan === 'Fiscal').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" }
      ]
    }
  }), [bilans]);

  const statCards = useMemo(() => [
    { labelKey: 'stat_total_bilans', value: stats.total, icon: Database, color: 'from-indigo-500 to-indigo-700' },
    { labelKey: 'stat_total_actif', value: fmtMoney(stats.totalActif), icon: Building2, color: 'from-green-500 to-green-700' },
    { labelKey: 'stat_total_passif', value: fmtMoney(stats.totalPassif), icon: BarChart3, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_capitaux_propres', value: fmtMoney(stats.capitauxPropres), icon: Wallet, color: 'from-emerald-500 to-emerald-600' },
    { labelKey: 'stat_fonds_roulement', value: fmtMoney(stats.fondsRoulement), icon: TrendingUp, color: 'from-amber-500 to-amber-600' },
  ], [stats]);

  const statutSelectOptions = useMemo(() => [
    { value: 'Provisoire', label: p('status_provisoire'), isDefault: true },
    { value: 'Définitif', label: p('status_definitif'), isDefault: true },
    { value: 'Validé', label: p('status_valide'), isDefault: true },
    { value: 'Audité', label: p('status_audite'), isDefault: true },
    { value: 'Certifié', label: p('status_certifie'), isDefault: true }
  ], [p]);

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'Provisoire', labelKey: 'status_provisoire', icon: AlertTriangle, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'Définitif', labelKey: 'status_definitif', icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Validé', labelKey: 'status_valide', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Audité', labelKey: 'status_audite', icon: Award, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'Certifié', labelKey: 'status_certifie', icon: Award, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ], p), [p]);

  const typeSelectOptions = useMemo(() => [
    { value: 'Simplifié', label: p('type_simplifie'), isDefault: true },
    { value: 'Normal', label: p('type_normal'), isDefault: true },
    { value: 'Consolidé', label: p('type_consolide'), isDefault: true },
    { value: 'Social', label: p('type_social'), isDefault: true },
    { value: 'Fiscal', label: p('type_fiscal'), isDefault: true }
  ], [p]);

  const currencySelectOptions = useMemo(() => getBrandCurrencyOptions().map((opt) => ({
    ...opt,
    label: opt.value === 'SAR' ? (p('currency_sar') || opt.label) : (p(`currency_${opt.value.toLowerCase()}`) || opt.label),
    isDefault: opt.value === getBrandDefaultCurrency() })), [p]);

  const distributionInfo = bilanDistribution[mainViewMode] || bilanDistribution.all_bilans;

  const filteredBilans = useMemo(() => {
    const base = bilans.filter(bilan => {

      const matchesSearch = !searchTerm ||
        bilan.bilan_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bilan.exercice_fiscal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bilan.statut_bilan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bilan.type_bilan?.toLowerCase().includes(searchTerm.toLowerCase());

      if (mainViewMode === 'all_bilans') {
        if (quickFilter === 'provisoire' && bilan.statut_bilan !== STATUT_FILTER_VALUES.provisoire) return false;
        if (quickFilter === 'valide' && bilan.statut_bilan !== STATUT_FILTER_VALUES.valide) return false;
        if (quickFilter === 'certifie' && bilan.statut_bilan !== STATUT_FILTER_VALUES.certifie) return false;
      }
      if (activeDistributionFilter) {
        if (mainViewMode === 'par_statut' || mainViewMode === 'all_bilans') {
          if (bilan.statut_bilan !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'par_type') {
          if (bilan.type_bilan !== activeDistributionFilter) return false;
        }
      }

      return matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [bilans, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredBilans, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(BILAN_LIST_COLUMNS, p);
  const bilanTableColSpan = visibleDataColumnCount + 2;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header - GPS Pattern */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Building2 className="h-8 w-8 text-green-400" />
            {p('title')}
            <button onClick={loadBilans} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={openNewBilanModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_bilan')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - GPS Glow Effect */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_bilans' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.nameKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterValue ? null : item.filterValue)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterValue ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterValue ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterValue ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('bilan_ref') ? (
                    <TableColumnHeaderFilter columnKey="bilan_ref" label={p('col_reference')} value={columnFilters.bilan_ref || ''} onChange={(v) => setColumnFilter('bilan_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('exercice_fiscal') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_exercice_fiscal')}</th>
                  ) : null}
                  {isColVisible('date_etablissement') ? (
                    <TableColumnHeaderFilter columnKey="date_etablissement" label={p('col_date_etablissement')} value={columnFilters.date_etablissement || ''} onChange={(v) => setColumnFilter('date_etablissement', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('total_actif') ? (
                    <TableColumnHeaderFilter columnKey="total_actif" label={p('col_total_actif')} value={columnFilters.total_actif || ''} onChange={(v) => setColumnFilter('total_actif', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('total_passif') ? (
                    <TableColumnHeaderFilter columnKey="total_passif" label={p('col_total_passif')} value={columnFilters.total_passif || ''} onChange={(v) => setColumnFilter('total_passif', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_bilan') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredBilans.length > 0 ? (
                  filteredBilans.map((bilan, index) => (
                    <tr 
                      key={bilan.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(bilan.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(bilan.id)}
                              onChange={() => handleSelectRecord(bilan.id)}
                            />
                          </div>
                        </td>
                      {isColVisible('bilan_ref') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{bilan.bilan_ref || p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{translateType(bilan.type_bilan)}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('exercice_fiscal') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{bilan.exercice_fiscal || p('na')}</span>
                      </td>
                      ) : null}
                      {isColVisible('date_etablissement') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">
                            {bilan.date_etablissement ? new Date(bilan.date_etablissement).toLocaleDateString(dateLocale) : p('na')}
                          </span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {bilan.periode_debut && bilan.periode_fin ? `${new Date(bilan.periode_debut).toLocaleDateString(dateLocale)} - ${new Date(bilan.periode_fin).toLocaleDateString(dateLocale)}` : p('period_undefined')}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('total_actif') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{bilan.total_actif ? formatBrandMoney(bilan.total_actif) : p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('label_total_actif')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('total_passif') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{bilan.total_passif ? formatBrandMoney(bilan.total_passif) : p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('label_total_passif')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('statut_bilan') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={bilan.statut_bilan || 'Provisoire'}
                            recordId={bilan.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(bilan)}
                          onEdit={() => handleEdit(bilan)}
                          onDelete={() => handleDelete(bilan.id)}
                          viewTitle={p('view')}
                          editTitle={p('edit')}
                          deleteTitle={p('delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={bilanTableColSpan} className="text-center py-12 text-white/60">
                      <Building2 size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_subtitle')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards View - GPS Pattern */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredBilans.length > 0 ? (
              filteredBilans.map((bilan, index) => (
                <motion.div
                  key={bilan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Building2 size={20} className="text-emerald-400" />
                          {bilan.bilan_ref || p('na')}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={bilan.statut_bilan || 'Provisoire'}
                          recordId={bilan.id}
                          options={statusBadgeOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info_bilan')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_reference')}</span> {bilan.bilan_ref}</div>
                            <div><span className="font-medium">{p('card_exercice')}</span> {bilan.exercice_fiscal}</div>
                            <div><span className="font-medium">{p('card_type')}</span> {translateType(bilan.type_bilan)}</div>
                            <div><span className="font-medium">{p('card_date')}</span> {bilan.date_etablissement ? new Date(bilan.date_etablissement).toLocaleDateString(dateLocale) : p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_actif')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_immobilise_net')}</span> {formatBrandMoney(bilan.actif_immobilise_net || 0)}</div>
                            <div><span className="font-medium">{p('card_circulant_net')}</span> {formatBrandMoney(bilan.actif_circulant_net || 0)}</div>
                            <div><span className="font-medium">{p('card_tresorerie')}</span> {formatBrandMoney(bilan.actif_tresorerie || 0)}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-bold text-white">{p('card_total_actif_full')} {formatBrandMoney(bilan.total_actif || 0)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_passif_capitaux')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_capitaux_propres')}</span> {formatBrandMoney(bilan.capitaux_propres_total || 0)}</div>
                            <div><span className="font-medium">{p('card_dettes_lt')}</span> {formatBrandMoney(bilan.passif_dettes_long_terme || 0)}</div>
                            <div><span className="font-medium">{p('card_dettes_ct')}</span> {formatBrandMoney(bilan.passif_dettes_court_terme || 0)}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-bold text-white">{p('card_total_passif_full')} {formatBrandMoney(bilan.total_passif || 0)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_ratios')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_fonds_roulement')}</span> {formatBrandMoney(bilan.fonds_roulement || 0)}</div>
                            <div><span className="font-medium">{p('card_bfr')}</span> {formatBrandMoney(bilan.besoin_fonds_roulement || 0)}</div>
                            <div><span className="font-medium">{p('card_tresorerie_nette')}</span> {formatBrandMoney(bilan.tresorerie_nette || 0)}</div>
                            <div><span className="font-medium">{p('card_ratio_liquidite')}</span> {(parseFloat(bilan.ratio_liquidite) || 0).toFixed(2)}</div>
                            <div><span className="font-medium">{p('card_ratio_solvabilite')}</span> {(parseFloat(bilan.ratio_solvabilite) || 0).toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(bilan)}
                      onEdit={() => handleEdit(bilan)}
                      onDelete={() => handleDelete(bilan.id)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Building2 size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - GPS ReactDOM Portal with 4 Collapsible Sections */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
                <Building2 className="h-6 w-6 text-green-400" />
                {selectedBilan ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => { void syncBilanFromEntries(); }}
                  className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all font-semibold flex items-center gap-2"
                >
                  <RefreshCcw size={16} />
                  {p('sync_from_entries', { defaultValue: 'من القيود' })}
                </button>
                <button 
                  type="submit" 
                  form="bilan-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedBilan ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={() => setShowModal(false)} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {bilanSituationFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = bilanSituationFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setBilanSituationFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="bilan-form" onSubmit={handleSubmit}>
                {bilanSituationFormTab === 'informationsBilan' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference_auto')}</label>
                            <input
                              type="text"
                              value={formData.bilan_ref}
                              onChange={(e) => setFormData({ ...formData, bilan_ref: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_ref')}
                            />
                          </div>
                          <ComptabiliteFiscalYearField
                            label={p('label_exercice_fiscal')}
                            value={formData.exercice_fiscal}
                            onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })}
                            placeholder={p('placeholder_exercice')}
                            required
                          />
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date_etablissement')}</label>
                            <CustomDatePicker
                              value={typeof formData.date_etablissement === 'string' ? formData.date_etablissement : ''}
                              onChange={(dateString) => setFormData({ ...formData, date_etablissement: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_debut')}</label>
                            <CustomDatePicker
                              value={typeof formData.periode_debut === 'string' ? formData.periode_debut : ''}
                              onChange={(dateString) => setFormData({ ...formData, periode_debut: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_fin')}</label>
                            <CustomDatePicker
                              value={typeof formData.periode_fin === 'string' ? formData.periode_fin : ''}
                              onChange={(dateString) => setFormData({ ...formData, periode_fin: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date_cloture')}</label>
                            <CustomDatePicker
                              value={typeof formData.date_cloture === 'string' ? formData.date_cloture : ''}
                              onChange={(dateString) => setFormData({ ...formData, date_cloture: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_statut_bilan')}</label>
                            <CustomSelect
                              value={formData.statut_bilan}
                              onChange={(val) => setFormData({ ...formData, statut_bilan: val })}
                              options={statutSelectOptions}
                              placeholder={p('select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type_bilan')}</label>
                            <CustomSelect
                              value={formData.type_bilan}
                              onChange={(val) => setFormData({ ...formData, type_bilan: val })}
                              options={typeSelectOptions}
                              placeholder={p('select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_devise')}</label>
                            <CustomSelect
                              value={formData.devise}
                              onChange={(val) => setFormData({ ...formData, devise: val })}
                              options={currencySelectOptions}
                              placeholder={p('select')}
                              searchable
                            />
                          </div>
                  </div>
                </div>
                )}
                {bilanSituationFormTab === 'actifImmobilisations' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_actif_immobilise_brut')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.actif_immobilise_brut}
                              onChange={(v) => setFormData({ ...formData, actif_immobilise_brut: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_amortissement')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.actif_immobilise_amortissement}
                              onChange={(v) => setFormData({ ...formData, actif_immobilise_amortissement: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_actif_circulant_brut')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.actif_circulant_brut}
                              onChange={(v) => setFormData({ ...formData, actif_circulant_brut: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_provision_circulant')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.actif_circulant_provision}
                              onChange={(v) => setFormData({ ...formData, actif_circulant_provision: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tresorerie_actif')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.actif_tresorerie}
                              onChange={(v) => setFormData({ ...formData, actif_tresorerie: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_commentaires_actif')}</label>
                            <textarea
                              value={formData.commentaires_actif}
                              onChange={(e) => setFormData({ ...formData, commentaires_actif: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_notes_actif')}
                            />
                          </div>
</div>
                )}
                {bilanSituationFormTab === 'passifRatios' && (
                  <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_capital_social')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.capitaux_propres_capital}
                              onChange={(v) => setFormData({ ...formData, capitaux_propres_capital: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reserves')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.capitaux_propres_reserves}
                              onChange={(v) => setFormData({ ...formData, capitaux_propres_reserves: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_resultat')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.capitaux_propres_resultat}
                              onChange={(v) => setFormData({ ...formData, capitaux_propres_resultat: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_dettes_long_terme')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.passif_dettes_long_terme}
                              onChange={(v) => setFormData({ ...formData, passif_dettes_long_terme: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_dettes_court_terme')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.passif_dettes_court_terme}
                              onChange={(v) => setFormData({ ...formData, passif_dettes_court_terme: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_provisions_passif')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.passif_provisions}
                              onChange={(v) => setFormData({ ...formData, passif_provisions: v })}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_commentaires_passif')}</label>
                            <textarea
                              value={formData.commentaires_passif}
                              onChange={(e) => setFormData({ ...formData, commentaires_passif: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_notes_passif')}
                            />
                          </div>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_auditeur_nom')}</label>
                            <input
                              type="text"
                              value={formData.auditeur_nom}
                              onChange={(e) => setFormData({ ...formData, auditeur_nom: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_auditeur')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_certification')}</label>
                            <input
                              type="text"
                              value={formData.auditeur_certification}
                              onChange={(e) => setFormData({ ...formData, auditeur_certification: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_certification')}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_notes')}
                            />
                          </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && viewingBilan ? ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => { setShowViewModal(false); setViewingBilan(null); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Building2 className="h-6 w-6 text-green-400" />
                {p('view_title')}
              </h2>
              <button onClick={() => { setShowViewModal(false); setViewingBilan(null); }} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Section 1: Informations Bilan */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Building2 size={18} className="text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_info_bilan')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_reference')}</p>
                    <p className="text-white font-semibold">{viewingBilan.bilan_ref || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_exercice_fiscal')}</p>
                    <p className="text-white font-semibold">{viewingBilan.exercice_fiscal || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_date_etablissement')}</p>
                    <p className="text-white font-semibold">{viewingBilan.date_etablissement ? new Date(viewingBilan.date_etablissement).toLocaleDateString(dateLocale) : p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_status')}</p>
                    <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium border ${
                      viewingBilan.statut_bilan === 'Certifié' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      viewingBilan.statut_bilan === 'Validé' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    }`}>
                      {translateStatut(viewingBilan.statut_bilan)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sections 2 & 3: Actif and Passif */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Layers size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('view_actif')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_total_actif')}</p>
                      <p className="text-white font-bold text-xl">{viewingBilan.total_actif ? formatBrandMoney(viewingBilan.total_actif) : p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('card_immobilise_net')}</p>
                      <p className="text-white font-semibold">{formatBrandMoney(viewingBilan.actif_immobilise_net || 0)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('card_circulant_net')}</p>
                      <p className="text-white font-semibold">{formatBrandMoney(viewingBilan.actif_circulant_net || 0)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Database size={18} className="text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">{p('view_passif_capitaux')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_total_passif')}</p>
                      <p className="text-white font-bold text-xl">{viewingBilan.total_passif ? formatBrandMoney(viewingBilan.total_passif) : p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('card_capitaux_propres')}</p>
                      <p className="text-white font-semibold">{formatBrandMoney(viewingBilan.capitaux_propres_total || 0)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('card_dettes_lt')}</p>
                      <p className="text-white font-semibold">{formatBrandMoney(viewingBilan.passif_dettes_long_terme || 0)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4: Ratios */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Target size={18} className="text-violet-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_ratios_analyse')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_fonds_roulement')}</p>
                    <p className="text-white font-semibold">{formatBrandMoney(viewingBilan.fonds_roulement || 0)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('card_bfr')}</p>
                    <p className="text-white font-semibold">{formatBrandMoney(viewingBilan.besoin_fonds_roulement || 0)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_ratio_liquidite')}</p>
                    <p className="text-white font-semibold">{(parseFloat(viewingBilan.ratio_liquidite) || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_ratio_solvabilite')}</p>
                    <p className="text-white font-semibold">{(parseFloat(viewingBilan.ratio_solvabilite) || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => { setShowViewModal(false); setViewingBilan(null); }}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {t('common.close')}
            </button>
          </motion.div>
        </div>,
        document.body
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default BilanSituationPage;

